import classNames from 'classnames';
import { PanelRightClose } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { DetailsTab } from '@/Domain/components/Inbox/ConversationDetails/Tabs/DetailsTab/DetailsTab';
import { usePanelRevealWidth } from '@/Domain/components/Shared/Resizable/usePanelRevealWidth';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { SidebarCollapsedIcon } from '@/Domain/components/icons/SidebarCollapsed.icon';
import { useGetConversation } from '@/Domain/hooks/Conversation/useGetConversation.hook';

type Props = {
  isCollapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
};

export const ConversationDetails = ({ isCollapsed, onCollapsedChange }: Props) => {
  const { t } = useTranslation('inbox_v2');

  const { id } = useParams() as { id: string };

  const { conversation, isLoading } = useGetConversation(id);
  const { measureRef, frozenWidth } = usePanelRevealWidth(isCollapsed);

  if (!conversation) {
    return null;
  }

  return (
    <div
      ref={measureRef}
      className={classNames('w-full ml-auto flex flex-col overflow-hidden p-4', {
        '!bg-card flex items-center': isCollapsed,
      })}
    >
      {isCollapsed ? (
        <SidebarCollapsedIcon
          className="cursor-pointer text-muted-foreground"
          onClick={() => onCollapsedChange(false)}
        />
      ) : null}

      {isLoading && (
        <div className="flex flex-1 h-full justify-center items-center ">
          <Spinner className="h-7 w-7" />
        </div>
      )}

      {!isLoading && !isCollapsed && (
        <div
          className={classNames(
            'transition-opacity ease-in-out transform h-full flex flex-col custom-scroll',
            {
              'opacity-0': isCollapsed,
              'opacity-100 duration-700': !isCollapsed,
            },
          )}
          style={frozenWidth ? { width: frozenWidth } : undefined}
        >
          <div className="flex items-center gap-3 mb-3 pt-1">
            <PanelRightClose
              className="cursor-pointer text-muted-foreground hover:text-foreground size-4"
              onClick={() => onCollapsedChange(true)}
            />
            <h2 className="text-base font-semibold text-foreground">{t('tabs.details')}</h2>
          </div>
          <div className="flex-1 overflow-y-auto">
            <DetailsTab conversation={conversation} />
          </div>
        </div>
      )}
    </div>
  );
};
